// src/pages/Dashboard.js
import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isValid, eachWeekOfInterval, eachMonthOfInterval } from 'date-fns';
import { isChargeback, CHARGEBACK_WINDOW_DAYS } from '../data/chargebacks';
import { Link } from 'react-router-dom';

const TEAL = '#00d4a0';
const BLUE = '#378ADD';
const AMBER = '#f5a623';
const RED = '#ff6b6b';
const COLORS = [TEAL, BLUE, AMBER, RED, '#a78bfa', '#5DCAA5'];
const TT = { background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 8, fontSize: 11, color: '#fff' };

const INTERNET_IDS = ['gig', 'internet_premier', 'internet_assist'];
const MOBILE_IDS   = ['mobile_line1', 'mobile_lines2to10', 'mobile_line1_ild', 'mobile_lines2to10_ild'];
const TV_IDS       = ['tv_select', 'tv_choice'];

// "Activated" = has a valid Internet Usage Date / Network Activation Date.
// Sentinel/placeholder dates (1900-01-01, 1900-01-02, 1901-01-01, 2000-01-02, etc.) do NOT count.
// TV orders count as activated if the same customer has an activated internet order.
function hasValidActivationDate(o) {
  if (!o.date) return false;
  const d = String(o.date).slice(0, 10);
  if (d === '' || d <= '2020-01-01') return false;
  // Reject obvious placeholders explicitly
  const bad = ['1900-01-01', '1900-01-02', '1901-01-01', '2000-01-01', '2000-01-02'];
  if (bad.includes(d)) return false;
  return true;
}

function buildActivatedSet(orders) {
  // Collect customer names that have an activated internet order
  const activatedCustomers = new Set();
  orders.forEach(o => {
    if (INTERNET_IDS.includes(o.productId) && hasValidActivationDate(o) && o.customerName) {
      activatedCustomers.add(o.customerName.toLowerCase());
    }
  });
  return activatedCustomers;
}

function isActivated(o, activatedCustomers) {
  if (o.status === 'Cancelled') return false;
  // Internet / Mobile: check activation date directly
  if (hasValidActivationDate(o)) return true;
  // TV: activated if same customer has an activated internet order
  if (TV_IDS.includes(o.productId) && o.customerName && activatedCustomers.has(o.customerName.toLowerCase())) return true;
  return false;
}

const PERIODS = [
  { key: '1w',        label: '1 week' },
  { key: 'month',     label: 'This month' },
  { key: 'lastmonth', label: 'Last month' },
  { key: '3m',        label: '3 months' },
  { key: '1y',        label: '1 year' },
];

function getWrittenDate(o) {
  // Use writtenDate (Sales Enter Date) for dashboard tracking
  if (o.writtenDate && o.writtenDate !== '' && o.writtenDate !== '1901-01-01') {
    const d = new Date(o.writtenDate);
    if (isValid(d)) return d;
  }
  // Fall back to date field, then createdAt
  if (o.date && o.date !== '' && o.date !== '1901-01-01') {
    const d = new Date(o.date);
    if (isValid(d)) return d;
  }
  if (o.createdAt?.seconds) return new Date(o.createdAt.seconds * 1000);
  return null;
}

function getRange(period) {
  const now = new Date();
  switch (period) {
    case '1w':        return { start: startOfWeek(now, { weekStartsOn: 1 }), end: now };
    case 'month':     return { start: startOfMonth(now), end: now };
    case 'lastmonth': return { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)) };
    case '3m':        return { start: subMonths(now, 3), end: now };
    case '1y':        return { start: subMonths(now, 12), end: now };
    default:          return { start: startOfMonth(now), end: now };
  }
}

function MetricCard({ label, value, sub, color, active, onClick }) {
  return (
    <div style={{ ...S.metricCard, borderColor: active ? TEAL : '#1e1e2a', cursor: 'pointer' }} onClick={onClick}>
      <div style={S.metricLabel}>{label}</div>
      <div style={{ ...S.metricValue, color: color || '#fff' }}>{value}</div>
      {sub && <div style={S.metricSub}>{sub}</div>}
    </div>
  );
}

function MiniTracker({ title, color, written, completed, cancelled }) {
  const completePct = written > 0 ? Math.round((completed / written) * 100) : 0;
  const cancelPct   = written > 0 ? Math.round((cancelled / written) * 100) : 0;
  return (
    <div style={S.trackerCard}>
      <div style={{ ...S.trackerTitle, color }}>{title}</div>
      <div style={S.trackerStats}>
        <div style={S.trackerStat}>
          <div style={S.trackerVal}>{written.toLocaleString()}</div>
          <div style={S.trackerLbl}>Written</div>
        </div>
        <div style={S.trackerStat}>
          <div style={{ ...S.trackerVal, color: TEAL }}>{completed.toLocaleString()}</div>
          <div style={S.trackerLbl}>Activated</div>
        </div>
        <div style={S.trackerStat}>
          <div style={{ ...S.trackerVal, color: RED }}>{cancelled.toLocaleString()}</div>
          <div style={S.trackerLbl}>Cancelled</div>
        </div>
      </div>
      <div style={S.trackerBars}>
        <div style={S.barRow}>
          <div style={S.barLabel}>Activated</div>
          <div style={S.barTrack}>
            <div style={{ ...S.barFill, width: `${completePct}%`, background: TEAL }} />
          </div>
          <div style={S.barPct}>{completePct}%</div>
        </div>
        <div style={S.barRow}>
          <div style={S.barLabel}>Cancel</div>
          <div style={S.barTrack}>
            <div style={{ ...S.barFill, width: `${cancelPct}%`, background: RED }} />
          </div>
          <div style={S.barPct}>{cancelPct}%</div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [period, setPeriod]       = useState('month');
  const [activeMetric, setActiveMetric] = useState('all');

  useEffect(() => {
    async function load() {
      const snap = await getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc'), limit(10000)));
      setAllOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }
    load();
  }, []);

  const { start, end } = getRange(period);

  const orders = useMemo(() => allOrders.filter(o => {
    const d = getWrittenDate(o);
    return d && d >= start && d <= end;
  }), [allOrders, period]);

  // Build activated customer set for TV piggyback logic
  const activatedCustomers = useMemo(() => buildActivatedSet(orders), [orders]);

  // All orders totals
  const written   = orders.length;
  const completed = orders.filter(o => isActivated(o, activatedCustomers)).length;
  const cancelled = orders.filter(o => o.status === 'Cancelled').length;
  const pending   = written - completed - cancelled;
  const totalComm = orders.reduce((a, o) => a + (o.totalCommission || 0), 0);
  const cancelPct = written > 0 ? ((cancelled / written) * 100).toFixed(1) : 0;

  // Internet tracker
  const iOrders    = orders.filter(o => INTERNET_IDS.includes(o.productId));
  const iWritten   = iOrders.length;
  const iCompleted = iOrders.filter(o => isActivated(o, activatedCustomers)).length;
  const iCancelled = iOrders.filter(o => o.status === 'Cancelled').length;

  // Mobile tracker
  const mOrders    = orders.filter(o => MOBILE_IDS.includes(o.productId));
  const mWritten   = mOrders.length;
  const mCompleted = mOrders.filter(o => isActivated(o, activatedCustomers)).length;
  const mCancelled = mOrders.filter(o => o.status === 'Cancelled').length;

  // Chargebacks in this period — disconnect within window AND within 90d of activation
  const periodChargebacks = useMemo(() => {
    return allOrders.filter(o => {
      if (!isChargeback(o)) return false;
      const d = new Date(o.disconnectDate);
      return d >= start && d <= end;
    });
  }, [allOrders, period]);
  const chargebackCount  = periodChargebacks.length;
  const chargebackAmount = periodChargebacks.reduce((a, o) => a + (o.totalCommission || 0), 0);
  const chargebackRate   = written > 0 ? ((chargebackCount / written) * 100).toFixed(1) : 0;

  // Day-over-day trend
  const numBuckets = period === '1w' ? 7 : period === 'month' || period === 'lastmonth' ? 30 : period === '3m' ? 13 : 12;
  const bucketDays = period === '1w' ? 1 : period === 'month' || period === 'lastmonth' ? 1 : period === '3m' ? 7 : 30;

  const trendData = useMemo(() => {
    const buckets = [];
    for (let i = numBuckets - 1; i >= 0; i--) {
      const bEnd   = subDays(end, i * bucketDays);
      const bStart = subDays(bEnd, bucketDays - 1);
      bStart.setHours(0, 0, 0, 0);
      bEnd.setHours(23, 59, 59, 999);
      const label = bucketDays === 1 ? format(bEnd, 'MM/dd') : bucketDays === 7 ? format(bEnd, 'MM/dd') : format(bEnd, 'MMM');
      const bOrders = orders.filter(o => {
        const d = getWrittenDate(o);
        return d && d >= bStart && d <= bEnd;
      });
      buckets.push({
        label,
        Written: bOrders.length,
        Completed: bOrders.filter(o => isActivated(o, activatedCustomers)).length,
        Cancelled: bOrders.filter(o => o.status === 'Cancelled').length,
      });
    }
    return buckets;
  }, [orders, period]);

  // Product mix — count by product name
  const productMap = {};
  orders.forEach(o => {
    if (!o.productName) return;
    // Group mobile lines together
    let name = o.productName;
    if (MOBILE_IDS.includes(o.productId)) name = 'Mobile';
    else if (o.productId === 'gig') name = 'Gig';
    else if (o.productId === 'internet_premier') name = 'Premier';
    else if (o.productId === 'internet_assist') name = 'Advantage';
    else if (o.productId === 'tv_select') name = 'TV Select';
    else if (o.productId === 'tv_choice') name = 'Stream/TV';
    else if (o.productId === 'uld_plus') name = 'ULD+';
    productMap[name] = (productMap[name] || 0) + 1;
  });
  const productData = Object.entries(productMap)
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({ name, value }));

  // Top reps
  const repMap = {};
  orders.forEach(o => {
    if (!o.repName) return;
    if (!repMap[o.repName]) repMap[o.repName] = { name: o.repName, deals: 0, completed: 0, cancelled: 0, comm: 0 };
    repMap[o.repName].deals++;
    if (isActivated(o, activatedCustomers)) repMap[o.repName].completed++;
    if (o.status === 'Cancelled') repMap[o.repName].cancelled++;
    repMap[o.repName].comm += o.totalCommission || 0;
  });
  const topReps = Object.values(repMap).sort((a, b) => b.deals - a.deals).slice(0, 10);

  if (loading) return <div style={{ padding: 60, color: '#555' }}>Loading...</div>;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Dashboard</h1>
          <p style={S.sub}>Tracking by order written date · Spectrum</p>
        </div>
        {/* Period toggle */}
        <div style={S.periodRow}>
          {PERIODS.map(p => (
            <button key={p.key} style={{ ...S.periodBtn, ...(period === p.key ? S.periodBtnActive : {}) }}
              onClick={() => setPeriod(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Metric cards — clickable to highlight */}
      <div className="stat-grid" style={S.metrics}>
        <MetricCard label="Written" value={written.toLocaleString()} sub="Total orders" color="#fff"
          active={activeMetric === 'written'} onClick={() => setActiveMetric(activeMetric === 'written' ? 'all' : 'written')} />
        <MetricCard label="Activated" value={completed.toLocaleString()} sub={`${written > 0 ? ((completed/written)*100).toFixed(1) : 0}% of written`} color={TEAL}
          active={activeMetric === 'completed'} onClick={() => setActiveMetric(activeMetric === 'completed' ? 'all' : 'completed')} />
        <MetricCard label="Cancelled" value={cancelled.toLocaleString()} sub={`${cancelPct}% cancel rate`} color={RED}
          active={activeMetric === 'cancelled'} onClick={() => setActiveMetric(activeMetric === 'cancelled' ? 'all' : 'cancelled')} />
        <MetricCard label="Commissions" value={'$' + Math.round(totalComm).toLocaleString()} sub="Total earned" color={AMBER}
          active={activeMetric === 'commissions'} onClick={() => setActiveMetric(activeMetric === 'commissions' ? 'all' : 'commissions')} />
      </div>

      {/* Internet + Mobile trackers */}
      <div className="chart-grid" style={S.trackers}>
        <MiniTracker title="Internet orders" color={BLUE} written={iWritten} completed={iCompleted} cancelled={iCancelled} />
        <MiniTracker title="Mobile orders" color={TEAL} written={mWritten} completed={mCompleted} cancelled={mCancelled} />
      </div>

      {/* Chargebacks summary */}
      <Link to="/chargebacks" style={{ textDecoration: 'none' }}>
        <div style={S.chargebackCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ ...S.trackerTitle, color: RED }}>Chargebacks · disconnect within {CHARGEBACK_WINDOW_DAYS}d</div>
            <span style={{ fontSize: 11, color: '#666' }}>View detail →</span>
          </div>
          <div style={S.cbGrid}>
            <div style={S.trackerStat}>
              <div style={{ ...S.trackerVal, color: RED }}>{chargebackCount.toLocaleString()}</div>
              <div style={S.trackerLbl}>Chargebacks</div>
            </div>
            <div style={S.trackerStat}>
              <div style={{ ...S.trackerVal, color: RED }}>${Math.round(chargebackAmount).toLocaleString()}</div>
              <div style={S.trackerLbl}>Commission lost</div>
            </div>
            <div style={S.trackerStat}>
              <div style={{ ...S.trackerVal, color: Number(chargebackRate) > 5 ? RED : AMBER }}>{chargebackRate}%</div>
              <div style={S.trackerLbl}>Of written</div>
            </div>
          </div>
        </div>
      </Link>

      {/* Trend chart */}
      <div style={S.chartCard}>
        <div style={S.chartTitle}>Orders written over time</div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fill: '#555', fontSize: 9 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fill: '#555', fontSize: 9 }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={TT} />
            {(activeMetric === 'all' || activeMetric === 'written')   && <Line type="monotone" dataKey="Written"   stroke="#fff"  strokeWidth={2} dot={false} />}
            {(activeMetric === 'all' || activeMetric === 'completed') && <Line type="monotone" dataKey="Completed" stroke={TEAL}  strokeWidth={2} dot={false} />}
            {(activeMetric === 'all' || activeMetric === 'cancelled') && <Line type="monotone" dataKey="Cancelled" stroke={RED}   strokeWidth={2} dot={false} />}
          </LineChart>
        </ResponsiveContainer>
        <div style={S.legend}>
          {(activeMetric === 'all' || activeMetric === 'written')   && <span style={S.legendItem}><span style={{ ...S.legendDot, background: '#fff' }} />Written</span>}
          {(activeMetric === 'all' || activeMetric === 'completed') && <span style={S.legendItem}><span style={{ ...S.legendDot, background: TEAL }} />Activated</span>}
          {(activeMetric === 'all' || activeMetric === 'cancelled') && <span style={S.legendItem}><span style={{ ...S.legendDot, background: RED }} />Cancelled</span>}
        </div>
      </div>

      {/* Product mix + status */}
      <div style={S.chartsRow}>
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Product mix</div>
          {productData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={productData} cx="50%" cy="50%" outerRadius={70} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false} fontSize={10}>
                    {productData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={TT} formatter={(v, n) => [v + ' orders', n]} />
                </PieChart>
              </ResponsiveContainer>
            </>
          ) : <div style={S.empty}>No data</div>}
        </div>

        <div style={S.chartCard}>
          <div style={S.chartTitle}>Order status breakdown</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={[
              { name: 'Written',   value: written },
              { name: 'Activated', value: completed },
              { name: 'Pending',   value: pending },
              { name: 'Cancelled', value: cancelled },
            ]} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fill: '#555', fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#555', fontSize: 9 }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TT} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {[written, completed, pending, cancelled].map((_, i) => (
                  <Cell key={i} fill={[BLUE, TEAL, AMBER, RED][i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top reps */}
      <div style={S.tableCard}>
        <div style={S.chartTitle}>Top reps by deals written</div>
        <table style={S.table}>
          <thead>
            <tr>{['#', 'Rep', 'Written', 'Activated', 'Cancelled', 'Cancel %', 'Commissions'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {topReps.map((rep, i) => {
              const cPct = rep.deals > 0 ? (rep.cancelled / rep.deals * 100).toFixed(0) : 0;
              return (
                <tr key={rep.name}>
                  <td style={{ ...S.td, color: i < 3 ? AMBER : '#555', fontWeight: 600, width: 36 }}>#{i + 1}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={S.avatar}>{rep.name[0]}</div>
                      {rep.name}
                    </div>
                  </td>
                  <td style={S.td}>{rep.deals}</td>
                  <td style={{ ...S.td, color: TEAL }}>{rep.completed}</td>
                  <td style={{ ...S.td, color: rep.cancelled > 0 ? RED : '#555' }}>
                    {rep.cancelled}
                  </td>
                  <td style={{ ...S.td, color: Number(cPct) > 20 ? RED : '#888' }}>{cPct}%</td>
                  <td style={{ ...S.td, color: AMBER }}>${Math.round(rep.comm).toLocaleString()}</td>
                </tr>
              );
            })}
            {topReps.length === 0 && <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: '#444', padding: 32 }}>No orders in this period</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 12, color: '#555', margin: '4px 0 0' },
  periodRow: { display: 'flex', gap: 6 },
  periodBtn: { background: '#13131a', border: '1px solid #2a2a3a', borderRadius: 7, padding: '6px 14px', color: '#666', fontSize: 12, cursor: 'pointer' },
  periodBtnActive: { background: '#1a2e24', borderColor: '#2a5a3a', color: TEAL },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 },
  metricCard: { background: '#13131a', border: '1px solid', borderRadius: 10, padding: '14px 18px', cursor: 'pointer', transition: 'border-color .15s' },
  metricLabel: { fontSize: 10, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 },
  metricValue: { fontSize: 26, fontWeight: 600, letterSpacing: '-0.5px', lineHeight: 1 },
  metricSub: { fontSize: 11, color: '#444', marginTop: 4 },
  trackers: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 },
  chargebackCard: { background: '#13131a', border: '1px solid #2a1515', borderRadius: 10, padding: '14px 18px', marginBottom: 14, cursor: 'pointer', transition: 'border-color .15s' },
  cbGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 },
  trackerCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, padding: '14px 18px' },
  trackerTitle: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 },
  trackerStats: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 },
  trackerStat: { textAlign: 'center' },
  trackerVal: { fontSize: 22, fontWeight: 600, color: '#fff' },
  trackerLbl: { fontSize: 10, color: '#555', marginTop: 2 },
  trackerBars: { display: 'flex', flexDirection: 'column', gap: 6 },
  barRow: { display: 'flex', alignItems: 'center', gap: 8 },
  barLabel: { fontSize: 10, color: '#555', width: 50 },
  barTrack: { flex: 1, background: '#1a1a24', borderRadius: 3, height: 5, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 3, transition: 'width .4s' },
  barPct: { fontSize: 10, color: '#666', width: 30, textAlign: 'right' },
  chartCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, padding: '14px 18px', marginBottom: 14 },
  chartTitle: { fontSize: 10, color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 },
  legend: { display: 'flex', gap: 16, marginTop: 8 },
  legendItem: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#888' },
  legendDot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block' },
  chartsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 },
  tableCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, padding: '14px 18px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '7px 10px', fontSize: 10, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e1e2a' },
  td: { padding: '9px 10px', fontSize: 13, color: '#ccc', borderBottom: '1px solid #111' },
  avatar: { width: 26, height: 26, borderRadius: '50%', background: '#1e2a24', border: '1px solid #00d4a0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: '#00d4a0', flexShrink: 0 },
  empty: { color: '#444', fontSize: 13, textAlign: 'center', padding: 40 },
};
